// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { debounce } from "es-toolkit";
import Loader from "@/modules/shared/ui/Loader";
import { IModelBaseInfo } from "@/lib/backend/models.generated.types";
import withLingui, { type LinguiProps } from "@/modules/shared/ui/withLingui";
import { msg } from "@lingui/core/macro";

// backend
import { getBackend } from "@/lib/backend/backend2";
import { waitForManufacturingAsync, getPlantsForUser } from "@/lib/businessLogic";

// import subcomponents
import Model from "./Model";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { classNames } from "@/lib/util";
import { IPlantData } from "@/lib/backend/manufacturing2.generated.types";

import { ResourceEditFiltersState } from "@/store/reducers/resourceEditFiltersReducer";
import ExportLineGroupMaterialsModal from "./ModelsExportLineGroupMaterialsModal";

import List from "react-virtualized/dist/es/List";
import type { ListRowProps } from "react-virtualized";

import "react-virtualized/styles.css";
import "./modelsPageLayout.scss";

const SIDEBAR_LIST_ROW_HEIGHT = 56;
const SIDEBAR_LIST_WIDTH = 232;
const FILTER_MODELS_DEBOUNCE_TIME_MS = 350;

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
    resource_edit_filters: ResourceEditFiltersState;
} & LinguiProps;

type State = {
    plants: IPlantData[];
    selectedPlantExternalId: string;
    models: IModelBaseInfo[];
    selected: IModelBaseInfo | null;
    materialExternalId: string;
    lineExternalId: string;
    error: string;
    warning: string;
    showExportMaterialsModal: boolean;
    handleParamsFromUrl: boolean;
    filteredModels: IModelBaseInfo[];
    searchValue: string;
    isLoading: boolean;
    listHeight: number;
    listWidth: number;
};

/**
 * Displaying production lines list.
 */
class Models extends Component<Props, State> {
    sidebarListRef = React.createRef<HTMLDivElement>();

    state: State = {
        models: [],
        plants: [],
        selectedPlantExternalId: "",
        lineExternalId: "",
        materialExternalId: "",
        error: "",
        warning: "",
        selected: null,
        showExportMaterialsModal: false,
        handleParamsFromUrl: true,
        filteredModels: [],
        searchValue: "",
        isLoading: false,
        listHeight: 0,
        listWidth: SIDEBAR_LIST_WIDTH,
    };

    updateListDimensions = () => {
        const listEl = this.sidebarListRef.current;
        if (!listEl) return;

        const { offsetHeight, offsetWidth } = listEl;
        if (offsetHeight !== this.state.listHeight || offsetWidth !== this.state.listWidth) {
            this.setState({ listHeight: offsetHeight, listWidth: offsetWidth });
        }
    };

    sortHits(hits: IModelBaseInfo[]): IModelBaseInfo[] {
        return hits.sort((a, b) => {
            if (a.material_external_id != b.material_external_id) {
                return a.material_external_id.localeCompare(b.material_external_id);
            }
            if (a.process_num != b.process_num) {
                return a.process_num.localeCompare(b.process_num);
            }
            return a.line_external_id.localeCompare(b.line_external_id);
        });
    }

    async loadComponent() {
        try {
            await waitForManufacturingAsync();
            const plants = getPlantsForUser();
            const selectedPlantExternalId = plants.length > 0 ? plants[0].external_id : "";
            this.setState(
                {
                    plants,
                    selectedPlantExternalId,
                    error: "",
                    warning: "",
                },
                () => {
                    this.setState({ isLoading: true });
                    this.loadModels()
                        .catch(e => this.setState({ error: (e as Error).message }))
                        .finally(() => this.setState({ isLoading: false }));
                }
            );
        } catch (e) {
            this.setState({ error: (e as Error).message });
        }
    }

    async loadModels() {
        const backend = getBackend();

        this.setState({ error: "", warning: "", selected: null, models: [] });

        // check if model is provided via url
        let targetPlant = this.state.selectedPlantExternalId;
        let targetLine: string | null = null;
        let targetMaterial: string | null = null;
        let targetProcessNum: string | null = null;

        if (this.state.handleParamsFromUrl) {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            targetLine = urlParams.get("line");
            targetMaterial = urlParams.get("material");
            targetProcessNum = urlParams.get("process_num");

            if (targetLine != null && targetLine !== "") {
                try {
                    const { lines } = await backend.manufacturing.getLines({ external_id: targetLine });
                    if (lines.length > 0) {
                        targetPlant = lines[0].plant_external_id;
                    }
                    this.setState({
                        handleParamsFromUrl: false,
                        lineExternalId: targetLine,
                        materialExternalId: targetMaterial ?? "",
                    });
                } catch (err) {
                    this.setState({ warning: (err as Error).message });
                }
            } else {
                this.setState({ handleParamsFromUrl: false });
            }
        }

        try {
            let { hits } = await backend.models.searchModels({
                plant: targetPlant,
                material: targetMaterial ?? undefined,
                line: targetLine ?? undefined,
                process_num: targetProcessNum ?? undefined,
            });

            hits = this.sortHits(hits);

            if (hits.length === 0) {
                this.setState({
                    warning: this.props.i18n._(msg`No models found`),
                });
                return;
            }

            const selected =
                targetLine != null && targetMaterial != null && targetProcessNum != null
                    ? (hits.find(
                          hit =>
                              hit.line_external_id === targetLine &&
                              hit.material_external_id === targetMaterial &&
                              hit.process_num === targetProcessNum
                      ) ?? hits[0])
                    : hits[0];

            this.setState({
                models: hits,
                filteredModels: hits,
                selected,
                selectedPlantExternalId: targetPlant,
            });
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    async componentDidMount() {
        window.addEventListener("resize", this.updateListDimensions);
        await this.loadComponent();
        this.updateListDimensions();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateListDimensions);
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        if (prevState.isLoading && !this.state.isLoading) {
            this.updateListDimensions();
        }

        const { materialExternalId, lineExternalId, searchValue } = this.state;

        if (
            prevState.materialExternalId !== materialExternalId ||
            prevState.lineExternalId !== lineExternalId ||
            prevState.searchValue !== searchValue
        ) {
            this.debouncedFilterModels();
        }
    }

    isModelSelected = (model: IModelBaseInfo): boolean => {
        const { selected } = this.state;
        return (
            selected !== null &&
            selected.material_external_id === model.material_external_id &&
            selected.line_external_id === model.line_external_id &&
            selected.process_num === model.process_num
        );
    };

    renderModelRow = ({ index, key, style }: ListRowProps) => {
        const model = this.state.filteredModels[index];
        return (
            <a
                key={key}
                style={style}
                href="#"
                className={classNames("sidebar-link", { active: this.isModelSelected(model) })}
                onClick={e => {
                    e.preventDefault();
                    this.setSelected(model);
                }}
            >
                <span className="sidebar-link-title ellipsis-truncate-text">{model.material_external_id}</span>
                <span className="sidebar-link-subtitle ellipsis-truncate-text">
                    [{model.process_num}] {model.line_external_id}
                </span>
            </a>
        );
    };

    setSelected(selected: IModelBaseInfo) {
        this.setState({ selected });
    }

    renderExportMaterialsModal() {
        return (
            <ExportLineGroupMaterialsModal
                show={this.state.showExportMaterialsModal}
                onHide={this.closeExportMaterialsModal}
                modelsInfo={this.state.models}
            />
        );
    }

    showExportMaterialsModal() {
        this.setState({ showExportMaterialsModal: true });
    }

    closeExportMaterialsModal = () => {
        this.setState({ showExportMaterialsModal: false });
    };

    async handleLnesMaterialsSearchChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        const searchValue = event.currentTarget.value.trim();
        this.setState({ searchValue });
    }

    debouncedFilterModels = debounce(() => {
        if (!this.state.searchValue) {
            this.setState({ filteredModels: this.state.models });
            return;
        }
        this.setState({ filteredModels: this.state.models.filter(this.filterModels) });
    }, FILTER_MODELS_DEBOUNCE_TIME_MS);

    filterModels = (value: IModelBaseInfo): boolean => {
        return (
            value.material_external_id.includes(this.state.searchValue) ||
            value.line_external_id.includes(this.state.searchValue)
        );
    };

    async handlePlantsSearchChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        // read new values
        const target = event.currentTarget;
        const value = target.value;
        const name = target.name;

        if (name == "plantExternalId") {
            const selectedPlant = this.state.plants.find(x => x.external_id === value);
            if (selectedPlant !== undefined) {
                this.setState({ selectedPlantExternalId: selectedPlant.external_id }, () => {
                    this.loadModels().catch(e => this.setState({ error: (e as Error).message }));
                });
            } else {
                this.setState({ selectedPlantExternalId: "" }, () => {
                    this.loadModels().catch(e => this.setState({ error: (e as Error).message }));
                });
            }
            return;
        }
    }

    render() {
        if (this.state.isLoading) {
            return <Loader />;
        }

        const { listWidth, listHeight, filteredModels } = this.state;
        const selectedIndex = filteredModels.findIndex(this.isModelSelected);

        const { i18n } = this.props;

        return (
            <main className="models-page-layout">
                <div className="header-section header-section--sidebar">
                    <input
                        type="text"
                        className="form-control search-input"
                        placeholder={i18n._(msg`Search lines/materials`)}
                        name="all"
                        value={this.state.searchValue}
                        onChange={e => this.handleLnesMaterialsSearchChange(e)}
                    />
                </div>
                <div className="header-section header-section--main d-flex justify-content-between gap-2">
                    <select
                        name="plantExternalId"
                        className="form-control select_filter_control react-select react-select--sm w-100"
                        value={this.state.selectedPlantExternalId}
                        onChange={e => this.handlePlantsSearchChange(e)}
                    >
                        {this.state.plants.map((plant, i) => {
                            return (
                                <option key={i} value={plant.external_id}>
                                    {plant.title}
                                </option>
                            );
                        })}
                    </select>

                    <Dropdown className="actions-dropdown">
                        <Dropdown.Toggle as="button" className="btn actions-btn" title={i18n._(msg`Show details`)}>
                            {i18n._(msg`Actions`)}
                            <i className="fas fa-chevron-down" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="right">
                            <Dropdown.Item as="button" onClick={() => this.showExportMaterialsModal()}>
                                {i18n._(msg`Export`)}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="sidebar-menu-section">
                    <div className="sidebar-menu-list mt-2">
                        <div ref={this.sidebarListRef} className="sidebar-menu-list-inner">
                            {this.state.filteredModels.length > 0 && this.state.listHeight > 0 && (
                                <List
                                    width={listWidth}
                                    height={listHeight}
                                    rowCount={filteredModels.length}
                                    rowHeight={SIDEBAR_LIST_ROW_HEIGHT}
                                    scrollToIndex={selectedIndex >= 0 ? selectedIndex : undefined}
                                    rowRenderer={this.renderModelRow}
                                    // React hack: pass filteredModels as a prop so the PureComponent List actually compares it.
                                    // rowData isn't used by List itself; it only drives the shallow prop check.
                                    rowData={filteredModels}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="main-section d-flex flex-column gap-3">
                    <ErrorComponent msg={this.state.error} type="error" />
                    <ErrorComponent msg={this.state.warning} type="warning" />
                    {this.state.selected && (
                        <Model
                            lineExternalId={this.state.selected.line_external_id}
                            materialExternalId={this.state.selected.material_external_id}
                            processNum={this.state.selected.process_num}
                        />
                    )}
                    {this.state.models.length > 0 && this.renderExportMaterialsModal()}
                </div>
            </main>
        );
    }
}

export default withLingui(Models);
